import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';

interface SearchResult {
  id: string;
  title: string;
  description?: string;
  url: string;
  type?: string;
}

interface SearchBoxProps {
  onSearch?: (query: string) => Promise<SearchResult[]>;
  placeholder?: string;
  className?: string;
}

export default function SearchBox({ 
  onSearch,
  placeholder = "Search...",
  className = ""
}: SearchBoxProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (query.trim() && onSearch) {
        setLoading(true);
        try {
          const searchResults = await onSearch(query);
          setResults(searchResults);
          setIsOpen(true);
        } catch (error) {
          console.error('Search error:', error);
          setResults([]);
        } finally {
          setLoading(false);
        }
      } else {
        setResults([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query, onSearch]);

  const handleClose = () => {
    setQuery('');
    setIsOpen(false);
    setResults([]);
  };

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full px-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F98B3D] focus:border-transparent"
          aria-label="Search"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        {query && (
          <button
            onClick={handleClose}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Clear search"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[80vh] overflow-y-auto z-50">
          <div className="divide-y divide-gray-200">
            {results.map((result) => (
              <a
                key={result.id}
                href={result.url}
                className="block hover:bg-gray-50 p-4 transition-colors duration-200"
                onClick={() => setIsOpen(false)}
              >
                <h4 className="text-base font-medium text-gray-900">{result.title}</h4>
                {result.description && (
                  <p className="text-sm text-gray-600 mt-1">{result.description}</p>
                )}
                {result.type && (
                  <span className="inline-block mt-2 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {result.type}
                  </span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {isOpen && loading && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F98B3D] mx-auto"></div>
        </div>
      )}

      {isOpen && !loading && results.length === 0 && query.trim() !== '' && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 text-center">
          <p className="text-gray-600">No results found</p>
        </div>
      )}
    </div>
  );
}